import React, { useState, useEffect } from "react";
import { MdClose, MdFileDownload, MdVisibility } from "react-icons/md";
import api from "../../api/axios";
import "../../assets/css/Deals/DealDocuments.css";

export default function DealDocumentPreviewModal({
  isOpen,
  onClose,
  document: doc,
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const isPdf =
    doc?.mime_type?.includes("pdf") ||
    doc?.original_filename?.toLowerCase().endsWith(".pdf");

  const isImage =
    doc?.mime_type?.startsWith("image/") ||
    [".png", ".jpg", ".jpeg", ".webp"].some((ext) =>
      doc?.original_filename?.toLowerCase().endsWith(ext)
    );

  useEffect(() => {
    let activeUrl = null;

    if (isOpen && doc?.id) {
      setLoading(true);
      setError(null);
      setBlobUrl(null);

      api
        .get(`/deal/documents/${doc.id}/preview`, { responseType: "blob" })
        .then((res) => {
          const url = URL.createObjectURL(res.data);
          activeUrl = url;
          setBlobUrl(url);
        })
        .catch((err) => {
          console.error("Preview load error:", err);
          setError("Unable to load preview for this document.");
        })
        .finally(() => {
          setLoading(false);
        });
    }

    return () => {
      if (activeUrl) {
        URL.revokeObjectURL(activeUrl);
      }
    };
  }, [isOpen, doc?.id]);

  if (!isOpen || !doc) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const res = await api.get(`/deal/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || `document_${doc.id}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download error:", err);
      // Fallback
      if (blobUrl) {
        const a = window.document.createElement("a");
        a.href = blobUrl;
        a.download = doc.original_filename || `document_${doc.id}.pdf`;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="doc-preview-overlay" onClick={onClose}>
      <div className="doc-preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="doc-preview-header">
          <div className="doc-preview-header-left">
            <MdVisibility size={20} color="#60a5fa" />
            <h4 className="doc-preview-title">{doc.original_filename}</h4>
            <span
              className={`doc-type-tag ${doc.doc_type?.toLowerCase() || "other"}`}
            >
              {doc.doc_type}
            </span>
          </div>

          <div className="doc-preview-header-actions">
            <button
              className="btn-upload-doc"
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                background: "#059669",
              }}
              onClick={handleDownload}
              disabled={downloading}
            >
              <MdFileDownload size={16} />
              {downloading ? "Downloading..." : "Download"}
            </button>
            <button className="doc-modal-close" onClick={onClose}>
              <MdClose />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="doc-preview-body">
          {loading ? (
            <div className="doc-preview-loading">
              <div className="doc-preview-spinner" />
              <p style={{ color: "#64748b", fontSize: "13px" }}>Loading document preview...</p>
            </div>
          ) : error ? (
            <div className="doc-preview-error">
              <h4 style={{ color: "#dc2626", margin: "0 0 8px" }}>Preview Error</h4>
              <p style={{ color: "#64748b", fontSize: "13px", margin: "0 0 16px" }}>{error}</p>
              <button className="btn-upload-doc" onClick={handleDownload}>
                <MdFileDownload size={16} />
                Try Download Instead
              </button>
            </div>
          ) : isPdf && blobUrl ? (
            <object
              data={blobUrl}
              type="application/pdf"
              className="doc-preview-frame"
            >
              <div className="doc-preview-unsupported">
                <h4>PDF Preview Unavailable</h4>
                <p>Your browser does not support inline PDF viewing.</p>
                <button className="btn-upload-doc" onClick={handleDownload}>
                  <MdFileDownload size={16} />
                  Download File
                </button>
              </div>
            </object>
          ) : isImage && blobUrl ? (
            <img
              src={blobUrl}
              alt={doc.original_filename}
              className="doc-preview-image"
            />
          ) : (
            <div className="doc-preview-unsupported">
              <h4>Direct Preview Not Available for this File Type</h4>
              <p>
                This document is formatted as a <strong>{doc.mime_type || "document"}</strong> file.
                Please download it to view locally.
              </p>
              <button className="btn-upload-doc" onClick={handleDownload}>
                <MdFileDownload size={16} />
                Download ({doc.file_size_formatted || "File"})
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

